// Libraries
import _ from 'lodash';
import React, {forwardRef, useImperativeHandle} from 'react';

// Supermove
import {gql, DocumentNode} from '@supermove/graphql';
import {useState} from '@supermove/hooks';
import {PayengineCreditCard, PaymentMethodModel, ProjectModel} from '@supermove/models';

// App
import DocumentV2ClientCreditCardList from '@shared/modules/Document/components/DocumentV2ClientCreditCardList';
import BeginPaymentV3Form from '@shared/modules/Payment/forms/BeginPaymentV3Form';
import {transactionContactMessage} from '@shared/modules/Payment/hooks/payengineErrors';
import useChargePayEngineCreditCardV3 from '@shared/modules/Payment/hooks/useChargePayEngineCreditCardV3';
import PaymentMethodKind from '@shared/modules/PaymentMethod/enums/PaymentMethodKind';

export type DocumentV2ChargeClientCardHandle = {
  submit: () => Promise<void>;
};

type DocumentV2ChargeClientCreditCardProps = {
  clientId: string;
  project: ProjectModel;
  documentName: string;
  paymentMethod: PaymentMethodModel;
  amountInCents: number;
  feeAmountInCents: number;
  allowSelectExistingPaymentMethods: boolean;
  onPaymentSuccess: ({
    paymentId,
    cardSummaryName,
  }: {
    paymentId: string;
    cardSummaryName: string;
  }) => void;
};

type DocumentV2ChargeClientCreditCardComponent = React.ForwardRefExoticComponent<
  DocumentV2ChargeClientCreditCardProps & React.RefAttributes<DocumentV2ChargeClientCardHandle>
> & {
  fragment: DocumentNode;
};

const DocumentV2ChargeClientCreditCard = forwardRef<
  DocumentV2ChargeClientCardHandle,
  DocumentV2ChargeClientCreditCardProps
>(
  (
    {
      project,
      documentName,
      paymentMethod,
      amountInCents,
      feeAmountInCents,
      allowSelectExistingPaymentMethods,
      onPaymentSuccess,
    },
    ref,
  ) => {
    const [errorTransactionMessage, setErrorTransactionMessage] = useState<string | null>(null);
    const [errorTokenizeMessage, setErrorTokenizeMessage] = useState<string | null>(null);
    const [selectedCreditCardId, setSelectedCreditCardId] = useState<string | 'new' | undefined>(
      undefined,
    );

    const paymentForm = BeginPaymentV3Form.forInvoice(project.mainMoveInvoice, {
      method: PaymentMethodKind.PAYENGINE_CREDIT_CARD,
      paymentMethodId: paymentMethod?.id,
      paymentFeeAmount: feeAmountInCents,
      amount: amountInCents + feeAmountInCents,
    });

    const {form, handleSubmit, setCreditCardClient} = useChargePayEngineCreditCardV3({
      beginPaymentV3Form: {
        ...paymentForm,
        name: `${paymentForm.name} - ${documentName}`,
        shouldErrorOnDuplicatePayment: false,
      },
      onSuccess: () => {
        setErrorTransactionMessage(null);
      },
      handleErrorMessage: (message) => {
        setErrorTransactionMessage(
          `${message} ${transactionContactMessage(project.organization.name)}`,
        );
      },
      handleTokenizeErrorMessage: setErrorTokenizeMessage,
    });

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (!selectedCreditCardId) {
          return Promise.resolve();
        }
        try {
          const response = await handleSubmit();
          onPaymentSuccess({
            paymentId: _.toString(response.payment.id),
            cardSummaryName: response.cardSummaryName,
          });
        } catch (error) {
          console.error(`Error charging credit card:`, error);
          return Promise.reject(new Error('Error charging credit card'));
        }
      },
    }));

    const field = `form.payengineCreditCardForm`;

    return (
      <React.Fragment>
        <DocumentV2ClientCreditCardList
          client={project.client}
          form={form}
          field={field}
          allowSelectExistingPaymentMethods={allowSelectExistingPaymentMethods}
          selectedCreditCardId={selectedCreditCardId}
          errorTransactionMessage={errorTransactionMessage}
          errorTokenizeMessage={errorTokenizeMessage}
          onSelect={(creditCard) => {
            setErrorTransactionMessage(null);
            setSelectedCreditCardId(creditCard?.id ?? 'new');
            setCreditCardClient(PayengineCreditCard.getCreditCardClient(creditCard));
          }}
          setCreditCardClient={setCreditCardClient}
        />
      </React.Fragment>
    );
  },
) as DocumentV2ChargeClientCreditCardComponent;

// --------------------------------------------------
// Data
// --------------------------------------------------

DocumentV2ChargeClientCreditCard.fragment = gql`
  ${BeginPaymentV3Form.forInvoice.fragment}
  ${DocumentV2ClientCreditCardList.fragment}
  ${PayengineCreditCard.getCreditCardClient.fragment}
  ${useChargePayEngineCreditCardV3.fragment}

  fragment DocumentV2ChargeClientCreditCard_Project on Project {
    id
    client {
      id
      primaryContact {
        id
      }
      creditCards {
        id
        ...useChargePayEngineCreditCardV3
        ...PayengineCreditCard_getCreditCardClient
      }
      ...DocumentV2ClientCreditCardList
    }
    mainMoveInvoice {
      id
      ...BeginPaymentV3Form_forInvoice
    }
  }

  fragment DocumentV2ChargeClientCreditCard_Document on Document {
    id
    job {
      id
      project {
        id
        ...DocumentV2ChargeClientCreditCard_Project
      }
    }
    invoice {
      id
      project {
        id
        ...DocumentV2ChargeClientCreditCard_Project
      }
    }
  }
`;

export default DocumentV2ChargeClientCreditCard;
